import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Plus, Sparkles, MapPinned, Search, X, RefreshCw, Pencil, Layers } from 'lucide-react';
import ModulLayout from '../layout/ModulLayout';
import ListToolbar, { PohledPill } from '../layout/ListToolbar';
import { KDoreseniSeznam } from '../layout/KDoreseni';
import ZpetTlacitko from '../ui/ZpetTlacitko';
import ModalShell from '../ui/ModalShell';
import usePotvrzeni from '../ui/usePotvrzeni';
import SidePanel from '../SidePanel';
import ZonyMapa from './ZonyMapa';
import { CasovkyKarta } from './CasovkyNastaveni';
import {
    SECTION_COLOR, SECTION_BG, BARVY_ZON, PASMA_DOJEZDU, hlavicky, nacti, kc, minut, cislo, datum, navrhniZonuPodleDojezdu,
} from './ui';

/**
 * Rozvoz → Zóny. Mnohoúhelníky v mapě s parametry rozvozu — cena, zdarma od,
 * minimum, slib, časová náročnost, priorita, dostupnost a platnost; všechno
 * pro každou zónu zvlášť (rozhodnutí 2026-10-09).
 *
 * Rozvržení (norma §1): seznam zón · mapa (detail, na desktopu pořád) ·
 * třetí sloupec `panelVzdy` — bez výběru přehled všech zón a „K dořešení",
 * s výběrem parametry, dojezd z mapy a skutečnost z objednávek. Úprava je
 * inspektor (§1f) nad třetím sloupcem — při ní je potřeba vidět mapu.
 * Na mobilu jen čtení (norma §1b: mobil je zobrazení a kontrola).
 *
 * Dojezd: mřížka bodů kolem bistra spočítaná přes Mapy.com (`rozvoz:dojezdy`)
 * se dá zobrazit jako pásma po 5 minutách a „Navrhnout podle dojezdu" z ní
 * udělá první tvar zóny.
 */
const DNES = () => new Date().toISOString().slice(0, 10);
const POHLEDY = [
    { id: 'aktivni', label: 'Aktivní', patri: (z) => z.aktivni && !(z.platnost_od && z.platnost_od > DNES()) },
    { id: 'planovane', label: 'Naplánované', patri: (z) => z.aktivni && z.platnost_od && z.platnost_od > DNES() },
    { id: 'archiv', label: 'Archiv', patri: (z) => !z.aktivni },
];
const DNY = ['Po', 'Út', 'St', 'Čt', 'Pá', 'So', 'Ne'];

/** Prázdná zóna pro nový záznam. */
const novaZona = (zony) => ({
    id: null, nazev: `Zóna ${zony.filter((z) => z.aktivni).length + 1}`,
    barva: BARVY_ZON[zony.length % BARVY_ZON.length], poradi: Math.max(0, ...zony.map((z) => z.poradi || 0)) + 1,
    polygon: [], cena_kc: null, zdarma_od_kc: null, minimum_kc: null, slib_min: null, narocnost_min: null, priorita: null,
    platnost_od: null, platnost_do: null, aktivni: true, poznamka: '', casy: [],
});

/** Dvojklik při kreslení přidá dva skoro stejné vrcholy — sloučit sousední shodné. */
const bezDuplicit = (p) => p.filter((b, i) => i === 0 || Math.abs(b[0] - p[i - 1][0]) > 1e-5 || Math.abs(b[1] - p[i - 1][1]) > 1e-5);

export default function RozvozZony({ isDark, isMobile, cardStyle, inputStyle, mutedColor, upravuje = false, rezervaDole = null }) {
    const [data, setData] = useState(null);       // { zony, bez_zony_30, bistro, dojezdy_spocteno_v }
    const [pohled, setPohled] = useState('aktivni');
    const [hledani, setHledani] = useState('');
    const [vybranaId, setVybranaId] = useState(null);
    const [edit, setEdit] = useState(null);       // rozpracovaná zóna v inspektoru
    const [rezim, setRezim] = useState('prohlizeni');
    const [dojezdy, setDojezdy] = useState(null);
    const [ukazatDojezdy, setUkazatDojezdy] = useState(false);
    const [navrh, setNavrh] = useState(false);
    const [hledanyBod, setHledanyBod] = useState(null);
    const [prizpusobit, setPrizpusobit] = useState(0);
    const [potvrdit, potvrzeni] = usePotvrzeni(isDark);

    const obnov = useCallback(() => nacti('/api/rozvoz/zony').then(setData).catch(() => setData({ zony: [] })), []);
    useEffect(() => { obnov(); }, [obnov]);
    useEffect(() => {
        if (!ukazatDojezdy && !navrh) return;
        if (dojezdy) return;
        nacti('/api/rozvoz/zony/dojezdy').then((d) => setDojezdy(d?.body || [])).catch(() => setDojezdy([]));
    }, [ukazatDojezdy, navrh, dojezdy]);

    // Horní hrana obsahu pro inspektor (norma §1f)
    const obsahRef = useRef(null);
    const [top, setTop] = useState(0);
    useEffect(() => {
        const zmer = () => { const el = obsahRef.current; if (el) setTop(Math.max(0, Math.round(el.getBoundingClientRect().top))); };
        zmer(); window.addEventListener('resize', zmer);
        return () => window.removeEventListener('resize', zmer);
    }, [edit, vybranaId]);

    const zony = data?.zony || [];
    const pocty = useMemo(() => Object.fromEntries(POHLEDY.map((p) => [p.id, zony.filter(p.patri).length])), [zony]);
    const akt = POHLEDY.find((p) => p.id === pohled) || POHLEDY[0];
    const q = hledani.trim().toLowerCase();
    const seznam = zony.filter((z) => akt.patri(z) && (!q || z.nazev.toLowerCase().includes(q)));
    const vybrana = zony.find((z) => z.id === vybranaId) || null;
    const cara = isDark ? 'rgba(255,255,255,0.06)' : 'var(--border-subtle)';
    const muted = mutedColor;
    const maxObjednavek = Math.max(1, ...zony.map((z) => z.objednavek_30 || 0));

    // Zóny na mapě: při úpravě se upravovaná kreslí editorem, ostatní normálně
    const zonyNaMape = useMemo(() => zony.filter((z) => pohled === 'archiv' ? true : z.aktivni), [zony, pohled]);

    // ── Úpravy ──
    // Stabilní callbacky — editor mapy podle nich staví značky vrcholů
    const naPolygon = useCallback((p) => setEdit((e) => (e ? { ...e, polygon: p } : e)), []);
    const naDokonceno = useCallback(() => { setEdit((e) => (e ? { ...e, polygon: bezDuplicit(e.polygon) } : e)); setRezim('uprava'); }, []);
    const zacniNovou = (polygon = []) => {
        const z = { ...novaZona(zony), polygon };
        setVybranaId(null);
        setEdit(z);
        setRezim(polygon.length >= 3 ? 'uprava' : 'kresleni');
    };
    const upravit = (z) => {
        setVybranaId(z.id);
        setEdit({ ...z, casy: (z.casy || []).map((c) => ({ den: c.den, od: String(c.od).slice(0, 5), do: String(c.do).slice(0, 5) })) });
        setRezim('uprava');
    };
    const zavritEdit = () => { setEdit(null); setRezim('prohlizeni'); };
    const ulozit = async () => {
        if (!edit || edit.polygon.length < 3) return { chyba: 'Zónu nejdřív nakresli do mapy — aspoň tři body.' };
        const telo = { ...edit };
        delete telo.id;
        ['cena_kc', 'zdarma_od_kc', 'minimum_kc', 'slib_min', 'narocnost_min', 'priorita'].forEach((k) => {
            telo[k] = telo[k] === '' || telo[k] == null ? null : Number(telo[k]);
        });
        telo.platnost_od = telo.platnost_od || null;
        telo.platnost_do = telo.platnost_do || null;
        const res = await fetch(edit.id ? `/api/rozvoz/zony/${edit.id}` : '/api/rozvoz/zony', {
            method: edit.id ? 'PUT' : 'POST', headers: hlavicky(), credentials: 'same-origin', body: JSON.stringify(telo),
        });
        const d = await res.json().catch(() => ({}));
        if (!res.ok) return { chyba: d?.message || d?.error || 'Uložení se nepovedlo.' };
        await obnov();
        setVybranaId(d.id);
        zavritEdit();
        return {};
    };
    const archivovat = async () => {
        if (!edit?.id) return;
        if (!(await potvrdit(`Archivovat ${edit.nazev}?`, { text: 'Zóna zmizí z mapy i z přiřazování nových objednávek. Starší objednávky na ni dál ukazují a zónu jde z archivu vrátit.', potvrdit: 'Archivovat' }))) return;
        await fetch(`/api/rozvoz/zony/${edit.id}`, { method: 'DELETE', headers: hlavicky(), credentials: 'same-origin' });
        await obnov();
        zavritEdit();
    };
    const obnovitZArchivu = async (z) => {
        await fetch(`/api/rozvoz/zony/${z.id}`, { method: 'PUT', headers: hlavicky(), credentials: 'same-origin', body: JSON.stringify({ aktivni: true }) });
        await obnov();
    };

    // ── Seznam ──
    const radek = (z, siroky) => {
        const sel = z.id === vybranaId;
        const meta = [
            z.cena_kc != null ? kc(z.cena_kc) : 'bez ceny',
            z.slib_min ? `slib ${z.slib_min} min` : null,
            z.dojezd_min_sec != null ? `dojezd ${Math.round(z.dojezd_min_sec / 60)}–${Math.round(z.dojezd_max_sec / 60)} min` : null,
        ].filter(Boolean).join(' · ');
        return (
            <button key={z.id} type="button" onClick={() => { if (edit) return; setVybranaId(z.id); setPrizpusobit((n) => n + 1); }} className="w-full text-left flex items-center"
                style={{ gap: 12, padding: isMobile ? '12px 16px' : '9px 14px', minHeight: 56, cursor: edit ? 'default' : 'pointer', border: 'none', borderBottom: `1px solid ${cara}`,
                    background: sel ? SECTION_BG : 'transparent', boxShadow: sel ? `inset 3px 0 0 ${SECTION_COLOR}` : 'none', opacity: z.aktivni ? 1 : 0.6 }}>
                <span style={{ width: 12, height: 12, borderRadius: 4, background: z.barva, flexShrink: 0 }} />
                <div className="flex-1 min-w-0">
                    <p className="truncate font-semibold" style={{ margin: 0, color: 'var(--text-primary)', fontSize: isMobile ? 15 : 13.5 }}>{z.nazev}</p>
                    <p className="truncate" style={{ margin: '2px 0 0', fontSize: 11.5, color: muted }}>{meta}</p>
                </div>
                {siroky && (
                    <span style={{ width: 120, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ flex: 1, height: 6, borderRadius: 3, background: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)', overflow: 'hidden' }}>
                            <span style={{ display: 'block', height: '100%', width: `${((z.objednavek_30 || 0) / maxObjednavek) * 100}%`, background: z.barva }} />
                        </span>
                    </span>
                )}
                <span style={{ width: 64, textAlign: 'right', fontSize: 12.5, fontWeight: 600, fontVariantNumeric: 'tabular-nums', color: 'var(--text-primary)', flexShrink: 0 }}>
                    {z.objednavek_30 || 0}
                </span>
            </button>
        );
    };

    const seznamPanel = ({ siroky }) => (
        <div className={isMobile ? 'flex flex-col flex-1 min-h-0' : 'rounded-xl overflow-hidden flex flex-col flex-1 min-h-0'} style={isMobile ? null : cardStyle}>
            <ListToolbar siroky={siroky} kompaktni={isMobile} sklo isDark={isDark} mutedColor={muted} inputStyle={inputStyle} barva={SECTION_COLOR}
                hledani={{ value: hledani, onChange: setHledani, placeholder: 'Hledat zónu…', placeholderKratky: 'Hledat' }}
                akce={upravuje && !isMobile ? [{ id: 'nova', label: 'Nová zóna', Icon: Plus, primarni: true, onClick: () => zacniNovou() }] : []}
                nastrojeAkce={upravuje && !isMobile ? [{ id: 'navrh', label: 'Navrhnout podle dojezdu', Icon: Sparkles, onClick: () => setNavrh(true) }] : null}
                pohled={POHLEDY.map((p) => (
                    <PohledPill key={p.id} label={p.label} pocet={pocty[p.id]} aktivni={akt.id === p.id}
                        onClick={() => setPohled(p.id)} isDark={isDark} mutedColor={muted} barva={SECTION_COLOR} kompaktni={isMobile} />
                ))} />
            <div className="flex items-center" style={{ gap: 12, padding: '8px 14px', fontSize: 10.5, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: muted, borderBottom: `1px solid ${cara}` }}>
                <span style={{ width: 12 }} /><span className="flex-1">Zóna</span>
                <span style={{ width: 64, textAlign: 'right', whiteSpace: 'nowrap' }}>30 dní</span>
            </div>
            <div className="flex-1" style={{ overflowY: 'auto', minHeight: 0 }}>
                {data === null && <p className="text-center text-sm" style={{ color: muted, padding: 32 }}>Načítám…</p>}
                {data && seznam.length === 0 && (
                    <p className="text-center text-sm" style={{ color: muted, padding: '32px 16px' }}>
                        {zony.length === 0 ? (upravuje ? 'Zatím žádné zóny. Nakresli první tlačítkem Nová zóna, nebo ji nech navrhnout podle dojezdu.' : 'Zatím žádné zóny.') : q ? 'Nic se neshoduje.' : 'V tomhle pohledu nic není.'}
                    </p>
                )}
                {seznam.map((z) => radek(z, siroky))}
                {rezervaDole && <div aria-hidden="true" style={{ height: rezervaDole }} />}
            </div>
        </div>
    );

    // ── Mapa (detail) ──
    const detailPanel = () => (
        <div className={isMobile ? 'flex flex-col flex-1 min-h-0' : 'rounded-xl overflow-hidden flex flex-col flex-1 min-h-0'} style={isMobile ? null : cardStyle}>
            {isMobile && (
                <div style={{ padding: '6px 14px 8px' }}>
                    <ZpetTlacitko onClick={() => setVybranaId(null)} popis="Zpět na zóny" sklo isDark={isDark} />
                </div>
            )}
            <div style={{ position: 'relative', flex: isMobile ? 'none' : 1, height: isMobile ? '48dvh' : 'auto', minHeight: 0 }}>
                <ZonyMapa zony={zonyNaMape} vybranaId={edit ? edit.id : vybranaId} onVyber={setVybranaId}
                    rezim={rezim} polygon={edit?.polygon || vybrana?.polygon || []} barva={edit?.barva || vybrana?.barva}
                    onPolygon={naPolygon} onDokonceno={naDokonceno}
                    dojezdy={ukazatDojezdy ? dojezdy : null} bistro={data?.bistro} hledanyBod={hledanyBod} prizpusobit={prizpusobit} isDark={isDark} />
                <OvladaniMapy isDark={isDark} muted={muted} ukazatDojezdy={ukazatDojezdy} setUkazatDojezdy={setUkazatDojezdy}
                    maDojezdy={!!data?.dojezdy_spocteno_v} hledanyBod={hledanyBod} setHledanyBod={setHledanyBod}
                    rezim={rezim} bodu={edit?.polygon?.length || 0} isMobile={isMobile} />
            </div>
            {isMobile && vybrana && (
                <div style={{ padding: '12px 16px', overflowY: 'auto', flex: 1, minHeight: 0 }}>
                    <PanelZony zona={vybrana} muted={muted} cara={cara} isDark={isDark} upravuje={false} />
                    {rezervaDole && <div aria-hidden="true" style={{ height: rezervaDole }} />}
                </div>
            )}
        </div>
    );

    // ── Třetí sloupec ──
    const panel = () => (
        <div className="rounded-xl" style={{ ...cardStyle, padding: 16 }}>
            {vybrana
                ? <PanelZony zona={vybrana} muted={muted} cara={cara} isDark={isDark} upravuje={upravuje}
                    onUpravit={() => upravit(vybrana)} onObnovit={() => obnovitZArchivu(vybrana)} onZavrit={() => setVybranaId(null)} />
                : <PrehledZon data={data} zony={zony} muted={muted} cara={cara} isDark={isDark} upravuje={upravuje}
                    onVyber={(id) => { setVybranaId(id); setPrizpusobit((n) => n + 1); }} onPrepocitano={() => { setDojezdy(null); obnov(); }} />}
        </div>
    );

    return (
        <div ref={obsahRef} className="flex flex-col" style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
            <ModulLayout isMobile={isMobile} vybrano={isMobile ? !!vybranaId : true} mobilRezim mobilPanel={false} panelVzdy
                barva={SECTION_COLOR} isDark={isDark} seznam={seznamPanel} detail={detailPanel} panel={panel} />

            {edit && !isMobile && (
                <SidePanel open onClose={zavritEdit} title={null} modal={false} width={400} isDark={isDark} isMobile={false} accent={SECTION_COLOR} topOffset={top}>
                    <ZonaEditor zona={edit} setZona={setEdit} rezim={rezim} onPrekreslit={() => { setEdit((e) => ({ ...e, polygon: [] })); setRezim('kresleni'); }}
                        onUlozit={ulozit} onArchivovat={edit.id ? archivovat : null} onZavrit={zavritEdit}
                        isDark={isDark} muted={muted} cara={cara} inputStyle={inputStyle} />
                </SidePanel>
            )}

            {navrh && (
                <NavrhModal isDark={isDark} muted={muted} dojezdy={dojezdy} bistro={data?.bistro} maDojezdy={!!data?.dojezdy_spocteno_v}
                    onZavrit={() => setNavrh(false)}
                    onNavrh={(polygon) => { setNavrh(false); zacniNovou(polygon); }} />
            )}
            {potvrzeni}
        </div>
    );
}

/** Ovládání nad mapou: hledání adresy, vrstva dojezdů a nápověda při kreslení. */
function OvladaniMapy({ isDark, muted, ukazatDojezdy, setUkazatDojezdy, maDojezdy, hledanyBod, setHledanyBod, rezim, bodu, isMobile }) {
    const [adresa, setAdresa] = useState('');
    const [hledam, setHledam] = useState(false);
    const [chyba, setChyba] = useState(null);
    const sklo = {
        background: isDark ? 'rgba(10,6,18,0.88)' : 'rgba(255,255,255,0.94)',
        border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'var(--border-default)'}`,
        boxShadow: '0 4px 14px rgba(0,0,0,0.18)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
    };
    const hledej = async (e) => {
        e?.preventDefault();
        if (!adresa.trim()) return;
        setHledam(true); setChyba(null);
        try {
            const d = await nacti(`/api/rozvoz/zony/najdi?adresa=${encodeURIComponent(adresa.trim())}`);
            if (d.lat == null) { setChyba(d.duvod || 'Adresu se nepodařilo najít.'); setHledanyBod(null); }
            else setHledanyBod(d);
        } catch { setChyba('Hledání se nepovedlo.'); }
        setHledam(false);
    };
    return (
        <>
            <div style={{ position: 'absolute', top: 10, left: 10, right: 10, zIndex: 500, display: 'flex', flexDirection: 'column', gap: 6, pointerEvents: 'none', maxWidth: 420 }}>
                <form onSubmit={hledej} className="flex items-center" style={{ ...sklo, borderRadius: 999, height: 40, padding: '0 6px 0 14px', gap: 8, pointerEvents: 'auto' }}>
                    <Search size={15} style={{ color: muted, flexShrink: 0 }} />
                    <input value={adresa} onChange={(e) => setAdresa(e.target.value)} placeholder="Kam to je? Zadej adresu…"
                        style={{ flex: 1, minWidth: 0, background: 'transparent', border: 'none', outline: 'none', fontSize: 13.5, color: 'var(--text-primary)' }} />
                    {(adresa || hledanyBod) && (
                        <button type="button" aria-label="Zrušit hledání" onClick={() => { setAdresa(''); setHledanyBod(null); setChyba(null); }}
                            style={{ width: 30, height: 30, borderRadius: 999, border: 'none', background: 'transparent', color: muted, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <X size={15} />
                        </button>
                    )}
                </form>
                {(hledam || chyba || hledanyBod) && (
                    <div style={{ ...sklo, borderRadius: 12, padding: '9px 12px', fontSize: 12.5, pointerEvents: 'auto', color: 'var(--text-primary)' }}>
                        {hledam && <span style={{ color: muted }}>Hledám…</span>}
                        {!hledam && chyba && <span style={{ color: '#EF4444' }}>{chyba}</span>}
                        {!hledam && !chyba && hledanyBod && (hledanyBod.zona ? (
                            <span>
                                <b style={{ color: hledanyBod.zona.barva }}>●</b> <b>{hledanyBod.zona.nazev}</b>
                                {' · '}doprava {hledanyBod.doprava?.cena != null ? kc(hledanyBod.doprava.cena) : 'bez ceny'}
                                {hledanyBod.zona.slib_min ? ` · slib ${hledanyBod.zona.slib_min} min` : ''}
                                {hledanyBod.dojezd_sec != null ? ` · jízda ${minut(hledanyBod.dojezd_sec)}` : ''}
                            </span>
                        ) : (
                            <span><b style={{ color: '#EF4444' }}>Mimo zóny</b>{hledanyBod.dojezd_sec != null ? ` · jízda ${minut(hledanyBod.dojezd_sec)}` : ''}</span>
                        ))}
                    </div>
                )}
                {rezim === 'kresleni' && (
                    <div style={{ ...sklo, borderRadius: 12, padding: '9px 12px', fontSize: 12.5, color: 'var(--text-primary)' }}>
                        {bodu < 3 ? `Klikáním do mapy přidávej body hranice (${bodu} z min. 3).` : 'Zónu uzavři kliknutím na první bod nebo dvojklikem.'}
                    </div>
                )}
                {rezim === 'uprava' && !isMobile && (
                    <div style={{ ...sklo, borderRadius: 12, padding: '9px 12px', fontSize: 12, color: muted }}>
                        Táhni body · „+" přidá bod · pravé tlačítko bod smaže · u sousední zóny se bod přichytí.
                    </div>
                )}
            </div>
            {maDojezdy && (
                <div style={{ position: 'absolute', bottom: 12, left: 10, zIndex: 500, display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-start' }}>
                    {ukazatDojezdy && (
                        <div style={{ ...sklo, borderRadius: 10, padding: '8px 10px', display: 'flex', gap: 10, fontSize: 11.5, color: 'var(--text-primary)', flexWrap: 'wrap' }}>
                            {PASMA_DOJEZDU.map(([m, o]) => (
                                <span key={m} className="inline-flex items-center" style={{ gap: 5 }}>
                                    <span style={{ width: 12, height: 12, borderRadius: 3, background: SECTION_COLOR, opacity: o }} />do {m} min
                                </span>
                            ))}
                        </div>
                    )}
                    <button type="button" onClick={() => setUkazatDojezdy((v) => !v)} aria-pressed={ukazatDojezdy}
                        className="inline-flex items-center" style={{ ...sklo, borderRadius: 999, height: 36, padding: '0 14px', gap: 6, fontSize: 12.5, fontWeight: 600, cursor: 'pointer',
                            color: ukazatDojezdy ? '#0A0612' : 'var(--text-primary)', ...(ukazatDojezdy ? { background: SECTION_COLOR } : null) }}>
                        <Layers size={14} /> Dojezd z bistra
                    </button>
                </div>
            )}
        </>
    );
}

/** Třetí sloupec bez výběru: všechny zóny v číslech + „K dořešení". */
function PrehledZon({ data, zony, muted, cara, isDark, upravuje, onVyber, onPrepocitano }) {
    const [pocitam, setPocitam] = useState(false);
    const [chyba, setChyba] = useState(null);
    const aktivni = zony.filter((z) => z.aktivni);
    const vZonach = aktivni.reduce((s, z) => s + (z.objednavek_30 || 0), 0);
    const bez = data?.bez_zony_30 || 0;
    const kDoreseni = [
        ...(bez > 0 ? [{ id: 'bez', problem: aktivni.length > 0, titulek: `${bez} objednávek mimo zóny`, popis: 'za 30 dní — adresy s GPS, které žádná zóna nepokrývá' }] : []),
        ...aktivni.filter((z) => z.cena_kc == null || !z.slib_min).map((z) => ({ id: `par-${z.id}`, zonaId: z.id, problem: false,
            titulek: z.nazev, popis: [z.cena_kc == null ? 'chybí cena dopravy' : null, !z.slib_min ? 'chybí slib' : null].filter(Boolean).join(' · ') })),
        ...(aktivni.length && !data?.dojezdy_spocteno_v ? [{ id: 'doj', problem: false, titulek: 'Dojezdy nejsou spočítané', popis: 'bez nich zóny neznají nejkratší a nejdelší dojezd' }] : []),
    ];
    const prepocitat = async () => {
        setPocitam(true); setChyba(null);
        const res = await fetch('/api/rozvoz/zony/dojezdy/prepocitat', { method: 'POST', headers: hlavicky(), credentials: 'same-origin', body: '{}' });
        const d = await res.json().catch(() => ({}));
        if (!res.ok) setChyba(d?.error || 'Přepočet se nepovedl.');
        else onPrepocitano?.();
        setPocitam(false);
    };
    const r = (l, h) => (
        <div className="flex justify-between text-[13px]" style={{ minHeight: 30, alignItems: 'center', borderBottom: `1px solid ${cara}` }}>
            <span style={{ color: muted }}>{l}</span><b style={{ color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums' }}>{h}</b>
        </div>
    );
    const nadpis = (t, mt = 0) => <p style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: muted, margin: `${mt}px 0 6px` }}>{t}</p>;
    return (
        <>
            {nadpis('Zóny · 30 dní')}
            {r('Aktivních zón', aktivni.length)}
            {r('Objednávek v zónách', vZonach)}
            {r('Mimo zóny', bez)}
            {r('Tržby v zónách', kc(aktivni.reduce((s, z) => s + (z.trzby_30 || 0), 0)))}
            {nadpis('Dojezdy z bistra', 16)}
            <p className="text-xs" style={{ color: muted, margin: '0 0 8px' }}>
                {data?.dojezdy_spocteno_v ? `Spočítáno ${datum(data.dojezdy_spocteno_v)} přes Mapy.com (auto, provoz).` : 'Zatím nespočítáno.'}
            </p>
            {upravuje && (
                <button type="button" onClick={prepocitat} disabled={pocitam} className="inline-flex items-center gap-1.5 rounded-lg"
                    style={{ height: 34, padding: '0 12px', fontSize: 12.5, fontWeight: 600, cursor: pocitam ? 'wait' : 'pointer', background: 'transparent', color: 'var(--text-primary)',
                        border: `1px solid ${isDark ? 'rgba(255,255,255,0.14)' : 'var(--border-default)'}` }}>
                    <RefreshCw size={13} className={pocitam ? 'animate-spin' : ''} /> {pocitam ? 'Počítám… (asi 20 s)' : (data?.dojezdy_spocteno_v ? 'Přepočítat dojezdy' : 'Spočítat dojezdy')}
                </button>
            )}
            {chyba && <p className="text-xs" style={{ color: '#EF4444', margin: '6px 0 0' }}>{chyba}</p>}
            <CasovkyKarta zonaId={null} nazev="Osobní odběr" muted={muted} cara={cara} isDark={isDark} upravuje={upravuje} />
            {nadpis('K dořešení', 16)}
            <KDoreseniSeznam polozky={kDoreseni} isDark={isDark} prazdne="Zóny pokrývají všechny objednávky a mají vyplněné parametry."
                renderAkce={(k) => k.zonaId ? (
                    <button type="button" onClick={() => onVyber(k.zonaId)} style={{ fontSize: 12, fontWeight: 600, color: SECTION_COLOR, background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}>Otevřít</button>
                ) : null} />
        </>
    );
}

/** Třetí sloupec s vybranou zónou: parametry, dojezd z mapy, skutečnost. */
function PanelZony({ zona, muted, cara, isDark, upravuje, onUpravit, onObnovit, onZavrit }) {
    const [stat, setStat] = useState(null);
    useEffect(() => {
        setStat(null);
        nacti(`/api/rozvoz/zony/${zona.id}/statistika?dni=90`).then(setStat).catch(() => setStat({}));
    }, [zona.id, zona.updated_at]);
    const r = (l, h) => (
        <div className="flex justify-between text-[13px]" style={{ minHeight: 30, alignItems: 'center', borderBottom: `1px solid ${cara}`, gap: 12 }}>
            <span style={{ color: muted }}>{l}</span><b style={{ color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}>{h}</b>
        </div>
    );
    const nadpis = (t, mt = 0) => <p style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: muted, margin: `${mt}px 0 6px` }}>{t}</p>;
    const mapaMed = zona.dojezd_median_sec != null ? zona.dojezd_median_sec / 60 : null;
    const realMed = stat?.jizda?.median ?? null;
    const narocnost = mapaMed != null && realMed != null && stat.jizda.n >= 10 ? Math.round(realMed - mapaMed) : null;
    const dostupnost = (zona.casy || []).length
        ? DNY.map((d, i) => {
            const c = zona.casy.filter((x) => x.den === i + 1);
            return c.length ? `${d} ${c.map((x) => `${String(x.od).slice(0, 5)}–${String(x.do).slice(0, 5)}`).join(', ')}` : null;
        }).filter(Boolean).join(' · ')
        : 'celá provozní doba';
    return (
        <>
            <div className="flex items-start gap-2" style={{ marginBottom: 10 }}>
                <span style={{ width: 12, height: 12, borderRadius: 4, background: zona.barva, marginTop: 4, flexShrink: 0 }} />
                <div className="flex-1 min-w-0">
                    <p className="font-bold" style={{ margin: 0, fontSize: 15, color: 'var(--text-primary)' }}>{zona.nazev}</p>
                    <p className="text-xs" style={{ margin: '2px 0 0', color: muted }}>
                        {[`pořadí ${zona.poradi}`, zona.priorita ? `priorita ${zona.priorita}` : null, !zona.aktivni ? 'v archivu' : null,
                            zona.platnost_od ? `od ${datum(zona.platnost_od)}` : null, zona.platnost_do ? `do ${datum(zona.platnost_do)}` : null].filter(Boolean).join(' · ')}
                    </p>
                </div>
                {onZavrit && (
                    <button type="button" aria-label="Zavřít" onClick={onZavrit} style={{ background: 'none', border: 'none', color: muted, cursor: 'pointer', padding: 2 }}><X size={16} /></button>
                )}
            </div>
            {nadpis('Parametry')}
            {r('Cena dopravy', zona.cena_kc != null ? kc(zona.cena_kc) : '—')}
            {r('Zdarma od', zona.zdarma_od_kc != null ? kc(zona.zdarma_od_kc) : '—')}
            {r('Minimum objednávky', zona.minimum_kc != null ? kc(zona.minimum_kc) : '—')}
            {r('Slib doby dovozu', zona.slib_min ? `${zona.slib_min} min` : '—')}
            {r('Časová náročnost', zona.narocnost_min != null ? `${zona.narocnost_min > 0 ? '+' : ''}${zona.narocnost_min} min` : '—')}
            <p className="text-xs" style={{ color: muted, margin: '8px 0 0' }}>Dostupnost: {dostupnost}</p>
            <CasovkyKarta key={zona.id} zonaId={zona.id} nazev={zona.nazev} muted={muted} cara={cara} isDark={isDark} upravuje={upravuje && zona.aktivni} />

            {nadpis('Dojezd z bistra · mapa', 16)}
            {zona.dojezd_median_sec != null ? (
                <>
                    <DojezdPruh min={zona.dojezd_min_sec / 60} med={mapaMed} max={zona.dojezd_max_sec / 60} isDark={isDark} />
                    <p className="text-xs" style={{ color: muted, margin: '4px 0 0' }}>
                        nejkratší {Math.round(zona.dojezd_min_sec / 60)} · typicky {Math.round(mapaMed)} · nejdelší {Math.round(zona.dojezd_max_sec / 60)} min
                    </p>
                </>
            ) : <p className="text-xs" style={{ color: muted, margin: 0 }}>Dojezdy zatím nejsou spočítané.</p>}

            {nadpis('Skutečnost · 90 dní', 16)}
            {!stat && <p className="text-xs" style={{ color: muted }}>Načítám…</p>}
            {stat && (
                <>
                    {stat.jizda?.n > 0 && (
                        <>
                            <DojezdPruh min={stat.jizda.min} med={stat.jizda.median} max={stat.jizda.p90} isDark={isDark} />
                            <p className="text-xs" style={{ color: muted, margin: '4px 0 8px' }}>
                                jízda k první zastávce: typicky {Math.round(stat.jizda.median)} · 90 % do {Math.round(stat.jizda.p90)} min ({stat.jizda.n} jízd)
                            </p>
                        </>
                    )}
                    {r('Objednávek', cislo(stat.pocet))}
                    {r('Tržby', kc(stat.trzby))}
                    {r('Doba dovozu (medián)', stat.dovoz_median != null ? `${Math.round(stat.dovoz_median)} min` : '—')}
                    {r('Do 60 minut', stat.do60 != null ? `${stat.do60} %` : '—')}
                    {r('Ve slíbeném čase', stat.slib != null ? `${stat.slib} %` : '—')}
                    {narocnost != null && (
                        <p className="text-xs" style={{ color: 'var(--text-primary)', margin: '10px 0 0' }}>
                            Navržená časová náročnost: <b>{narocnost > 0 ? '+' : ''}{narocnost} min</b>
                            <span style={{ color: muted }}> (skutečná jízda proti mapě)</span>
                        </p>
                    )}
                </>
            )}
            {upravuje && (
                <button type="button" onClick={zona.aktivni ? onUpravit : onObnovit} className="w-full inline-flex items-center justify-center gap-1.5 rounded-lg"
                    style={{ height: 38, marginTop: 16, fontSize: 13, fontWeight: 600, cursor: 'pointer', background: SECTION_COLOR, color: '#0A0612', border: 'none' }}>
                    {zona.aktivni ? <><Pencil size={14} /> Upravit zónu</> : 'Vrátit z archivu'}
                </button>
            )}
        </>
    );
}

/** Rozpětí dojezdu 0–45 min: pruh od–do, čárka = typická hodnota. */
function DojezdPruh({ min, med, max, isDark }) {
    const S = 45;
    const x = (m) => `${Math.max(0, Math.min(100, (m / S) * 100))}%`;
    return (
        <div style={{ position: 'relative', height: 18 }} aria-hidden="true">
            <div style={{ position: 'absolute', top: 8, left: 0, right: 0, height: 2, background: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.07)' }} />
            <div style={{ position: 'absolute', top: 5, height: 8, left: x(min), width: `calc(${x(max)} - ${x(min)})`, borderRadius: 4, background: `${SECTION_COLOR}40`, border: `1px solid ${SECTION_COLOR}` }} />
            <div style={{ position: 'absolute', top: 1, height: 16, width: 2, left: x(med), background: 'var(--text-primary)' }} />
        </div>
    );
}

/** Inspektor: parametry zóny + práce s tvarem v mapě. */
function ZonaEditor({ zona, setZona, rezim, onPrekreslit, onUlozit, onArchivovat, onZavrit, isDark, muted, cara, inputStyle }) {
    const [ukladam, setUkladam] = useState(false);
    const [chyba, setChyba] = useState(null);
    const zmen = (k) => (e) => setZona((z) => ({ ...z, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));
    const pole = (label, k, props = {}) => (
        <label className="flex flex-col" style={{ gap: 4 }}>
            <span style={{ fontSize: 11, color: muted }}>{label}</span>
            <input value={zona[k] ?? ''} onChange={zmen(k)} style={inputStyle} {...props} />
        </label>
    );
    const ulozit = async () => {
        setUkladam(true); setChyba(null);
        const r = await onUlozit();
        if (r?.chyba) setChyba(r.chyba);
        setUkladam(false);
    };
    const pridatCas = () => setZona((z) => ({ ...z, casy: [...(z.casy || []), { den: 1, od: '11:00', do: '21:00' }] }));
    const vsechnyDny = () => setZona((z) => ({ ...z, casy: [1, 2, 3, 4, 5, 6, 7].map((den) => ({ den, od: '11:00', do: '21:00' })) }));
    const zmenCas = (i, k, v) => setZona((z) => ({ ...z, casy: z.casy.map((c, j) => (j === i ? { ...c, [k]: k === 'den' ? Number(v) : v } : c)) }));
    const smazCas = (i) => setZona((z) => ({ ...z, casy: z.casy.filter((_, j) => j !== i) }));
    const nadpis = (t) => <p style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: muted, margin: '18px 0 8px' }}>{t}</p>;

    return (
        <div className="flex flex-col" style={{ height: '100%', minHeight: 0 }}>
            <div style={{ padding: '14px 16px 12px', borderBottom: `1px solid ${cara}`, display: 'flex', alignItems: 'center', gap: 10 }}>
                <MapPinned size={16} style={{ color: zona.barva }} />
                <h3 className="flex-1 min-w-0 truncate font-semibold" style={{ margin: 0, fontSize: 14.5, color: 'var(--text-primary)' }}>{zona.id ? `Upravit ${zona.nazev}` : 'Nová zóna'}</h3>
                <button type="button" aria-label="Zavřít" onClick={onZavrit} style={{ background: 'none', border: 'none', color: muted, cursor: 'pointer', padding: 4 }}><X size={18} /></button>
            </div>
            <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '4px 16px 16px' }}>
                {nadpis('Tvar v mapě')}
                <div className="flex items-center justify-between" style={{ gap: 8 }}>
                    <span className="text-xs" style={{ color: zona.polygon.length >= 3 ? 'var(--text-primary)' : '#EF4444' }}>
                        {rezim === 'kresleni' ? `Kreslíš — ${zona.polygon.length} bodů` : `${zona.polygon.length} bodů hranice`}
                    </span>
                    {rezim !== 'kresleni' && (
                        <button type="button" onClick={onPrekreslit} style={{ fontSize: 12, fontWeight: 600, color: SECTION_COLOR, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>Nakreslit znovu</button>
                    )}
                </div>

                {nadpis('Zóna')}
                <div className="flex flex-col" style={{ gap: 10 }}>
                    {pole('Název', 'nazev', { maxLength: 80 })}
                    <div className="flex flex-col" style={{ gap: 4 }}>
                        <span style={{ fontSize: 11, color: muted }}>Barva v mapě</span>
                        <div className="flex flex-wrap" style={{ gap: 6 }}>
                            {BARVY_ZON.map((b) => (
                                <button key={b} type="button" aria-label={`Barva ${b}`} onClick={() => setZona((z) => ({ ...z, barva: b }))}
                                    style={{ width: 26, height: 26, borderRadius: 7, background: b, cursor: 'pointer', border: zona.barva === b ? '2px solid var(--text-primary)' : '2px solid transparent' }} />
                            ))}
                        </div>
                    </div>
                    <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                        {pole('Pořadí při překryvu', 'poradi', { type: 'number', min: 1, max: 99 })}
                        {pole('Priorita v dávkách', 'priorita', { type: 'number', min: 1, max: 9, placeholder: '—' })}
                    </div>
                    <p className="text-xs" style={{ color: muted, margin: 0 }}>Kde se zóny překrývají, platí ta s nižším pořadím — vnitřní zóna má 1.</p>
                </div>

                {nadpis('Rozvoz')}
                <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    {pole('Cena dopravy (Kč)', 'cena_kc', { type: 'number', min: 0, step: 1, inputMode: 'numeric' })}
                    {pole('Zdarma od (Kč)', 'zdarma_od_kc', { type: 'number', min: 0, step: 1, inputMode: 'numeric' })}
                    {pole('Minimum objednávky (Kč)', 'minimum_kc', { type: 'number', min: 0, step: 1, inputMode: 'numeric' })}
                    {pole('Slib doby dovozu (min)', 'slib_min', { type: 'number', min: 5, max: 240, inputMode: 'numeric' })}
                    {pole('Časová náročnost (+ min)', 'narocnost_min', { type: 'number', min: -30, max: 120, inputMode: 'numeric' })}
                </div>

                {nadpis('Dostupnost')}
                {(zona.casy || []).length === 0 && <p className="text-xs" style={{ color: muted, margin: '0 0 8px' }}>Zóna platí celou provozní dobu.</p>}
                {(zona.casy || []).map((c, i) => (
                    <div key={i} className="flex items-center" style={{ gap: 6, marginBottom: 6 }}>
                        <select value={c.den} onChange={(e) => zmenCas(i, 'den', e.target.value)} style={{ ...inputStyle, width: 70 }}>
                            {DNY.map((d, k) => <option key={d} value={k + 1}>{d}</option>)}
                        </select>
                        <input type="time" value={c.od} onChange={(e) => zmenCas(i, 'od', e.target.value)} style={inputStyle} />
                        <input type="time" value={c.do} onChange={(e) => zmenCas(i, 'do', e.target.value)} style={inputStyle} />
                        <button type="button" aria-label="Smazat čas" onClick={() => smazCas(i)} style={{ background: 'none', border: 'none', color: muted, cursor: 'pointer', padding: 4 }}><X size={15} /></button>
                    </div>
                ))}
                <div className="flex" style={{ gap: 12 }}>
                    <button type="button" onClick={pridatCas} style={{ fontSize: 12, fontWeight: 600, color: SECTION_COLOR, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>+ Přidat čas</button>
                    {(zona.casy || []).length === 0 && <button type="button" onClick={vsechnyDny} style={{ fontSize: 12, fontWeight: 600, color: SECTION_COLOR, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>Po–Ne 11–21</button>}
                </div>

                {nadpis('Platnost')}
                <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    {pole('Od', 'platnost_od', { type: 'date' })}
                    {pole('Do', 'platnost_do', { type: 'date' })}
                </div>
                <p className="text-xs" style={{ color: muted, margin: '6px 0 0' }}>Naplánovaná změna cen: nová zóna s datem Od, stará s datem Do.</p>

                {nadpis('Poznámka')}
                <textarea value={zona.poznamka || ''} onChange={zmen('poznamka')} rows={3} style={{ ...inputStyle, height: 'auto', padding: 10, resize: 'vertical' }} />

                {chyba && <p className="text-xs" style={{ color: '#EF4444', margin: '12px 0 0' }}>{chyba}</p>}
            </div>
            <div style={{ padding: 12, borderTop: `1px solid ${cara}`, display: 'flex', gap: 8 }}>
                {onArchivovat && (
                    <button type="button" onClick={onArchivovat} className="rounded-lg"
                        style={{ height: 40, padding: '0 14px', fontSize: 13, cursor: 'pointer', background: 'transparent', color: 'var(--text-secondary)', border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'var(--border-default)'}` }}>
                        Archivovat
                    </button>
                )}
                <button type="button" onClick={ulozit} disabled={ukladam || rezim === 'kresleni'} className="flex-1 rounded-lg font-semibold"
                    style={{ height: 40, fontSize: 13, cursor: ukladam ? 'wait' : 'pointer', background: SECTION_COLOR, color: '#0A0612', border: 'none', opacity: rezim === 'kresleni' ? 0.5 : 1 }}>
                    {ukladam ? 'Ukládám…' : rezim === 'kresleni' ? 'Nejdřív uzavři zónu' : 'Uložit zónu'}
                </button>
            </div>
        </div>
    );
}

/** „Navrhnout podle dojezdu" — vybrat minuty, z mřížky vznikne první tvar zóny. */
function NavrhModal({ isDark, muted, dojezdy, bistro, maDojezdy, onZavrit, onNavrh }) {
    const [min, setMin] = useState(10);
    const polygon = useMemo(() => (dojezdy && bistro ? navrhniZonuPodleDojezdu(dojezdy, bistro, min) : []), [dojezdy, bistro, min]);
    return (
        <ModalShell onClose={onZavrit} isDark={isDark} ikona={Sparkles} barva={SECTION_COLOR} nadpis="Navrhnout zónu podle dojezdu" sirka={420}
            paticka={(
                <button type="button" disabled={polygon.length < 3} onClick={() => onNavrh(polygon)} className="w-full rounded-xl font-semibold"
                    style={{ height: 44, background: SECTION_COLOR, color: '#0A0612', border: 'none', cursor: polygon.length < 3 ? 'not-allowed' : 'pointer', opacity: polygon.length < 3 ? 0.5 : 1 }}>
                    Nakreslit zónu do {min} minut
                </button>
            )}>
            <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
                {!maDojezdy ? (
                    <p className="text-sm" style={{ margin: 0, color: muted }}>Dojezdy z bistra zatím nejsou spočítané. Spočítej je v pravém sloupci (Dojezdy z bistra).</p>
                ) : dojezdy === null ? (
                    <p className="text-sm" style={{ margin: 0, color: muted }}>Načítám dojezdy…</p>
                ) : (
                    <>
                        <p className="text-sm" style={{ margin: 0, color: muted }}>
                            Zóna obejme místa, kam auto z bistra dojede do zvoleného času. Tvar pak doladíš táhnutím bodů.
                        </p>
                        <div className="flex flex-wrap" style={{ gap: 8 }}>
                            {[5, 10, 15, 20, 25, 30].map((m) => (
                                <button key={m} type="button" onClick={() => setMin(m)}
                                    style={{ height: 36, padding: '0 14px', borderRadius: 999, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                                        background: min === m ? SECTION_COLOR : 'transparent', color: min === m ? '#0A0612' : 'var(--text-primary)',
                                        border: `1px solid ${min === m ? SECTION_COLOR : (isDark ? 'rgba(255,255,255,0.14)' : 'var(--border-default)')}` }}>
                                    do {m} min
                                </button>
                            ))}
                        </div>
                    </>
                )}
            </div>
        </ModalShell>
    );
}
